import { Globe, Eye, Compass, Gem } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Motion";
import SectionHeader from "@/components/SectionHeader";
import CTABanner from "@/components/CTABanner";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const CHAPTER_ICONS = [null, null, null];
const VALUES = [
  { icon: Eye },
  { icon: Compass },
  { icon: Globe },
  { icon: Gem },
];

const CHAPTERS = [
  {
    num: "01",
    title: "The Boutique Difference",
    text: "VCK is intentionally boutique. You work directly with a dedicated professional who learns how your business runs — not a rotating pool of anonymous assistants. Every detail, preference, and standard is remembered and respected.",
  },
  {
    num: "02",
    title: "Absolute Discretion",
    text: "Trust is the foundation of every engagement. Confidential business information is handled with care, protected through secure channels, and never discussed beyond the scope of your work together.",
  },
  {
    num: "03",
    title: "Uncompromising Precision",
    text: "From a perfectly formatted document to a flawlessly coordinated calendar, the standard is the same every time: accurate, polished, and on schedule. The details are the work.",
  },
];

const VALUES_EN = [
  { title: "Professionalism", text: "A polished, dependable standard in every interaction and deliverable." },
  { title: "Organization", text: "Structure and order brought to inboxes, calendars, files, and workflows." },
  { title: "Communication", text: "Clear, prompt, and professional — you always know where things stand." },
  { title: "Client Service", text: "Attentive, high-touch support shaped around each client's way of working." },
];

const PageHeader = () => {
  const { t } = useLang();
  return (
    <section data-testid="about-header" className="bg-alabaster pb-20 pt-40">
      <SEO
        title="About Us | VCK Business Lab"
        description="VCK Business Lab is a global virtual administrative and business support company serving clients across Canada, the United States, Europe, and worldwide."
        path="/about"
      />
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold-muted">{t("about.overline")}</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("about.line1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold-muted">{t("about.line2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {t("about.intro")}
          </span>
        </MaskedLine>
      </div>
    </section>
  );
};

const FoundingStory = () => {
  const { t } = useLang();
  return (
    <section data-testid="founding-story-section" className="bg-alabaster pb-24">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20">
        <Reveal>
          <div className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full rounded-xl border border-gold/40" />
            <img
              src={IMAGES.moody}
              alt="Dark, refined executive workspace"
              loading="lazy"
              className="relative aspect-[4/3] w-full rounded-xl object-cover shadow-2xl"
            />
          </div>
        </Reveal>
        <div>
          <SectionHeader testid="founding-story" overline={t("about.storyOverline")} title={t("about.storyTitle")} />
          <Reveal delay={0.15}>
            <p className="mt-6 text-base leading-relaxed text-slate-600">{t("about.storyP1")}</p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{t("about.storyP2")}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t, c } = useLang();
  const chapters = c.aboutChapters || CHAPTERS;
  return (
    <section data-testid="manifesto-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader testid="manifesto" overline={t("about.manOverline")} title={t("about.manTitle")} />
        <div className="mt-16">
          {chapters.map((ch, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${i + 1}`}
                className="grid gap-6 border-t border-obsidian/10 py-12 lg:grid-cols-12"
              >
                <p className="font-mono text-5xl font-light text-gold-muted/60 lg:col-span-2">{CHAPTERS[i].num}</p>
                <h3 className="font-serif text-3xl text-obsidian lg:col-span-4">{ch.title}</h3>
                <p className="text-base leading-relaxed text-slate-600 lg:col-span-6">{ch.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const GlobalReach = () => {
  const { t } = useLang();
  return (
    <section data-testid="global-reach-section" className="relative overflow-hidden bg-obsidian py-24 lg:py-32">
      <img
        src={IMAGES.skyline}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 to-obsidian/90" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          dark
          testid="global-reach"
          overline={t("about.globalOverline")}
          title={t("about.globalTitle")}
          copy={t("about.globalCopy")}
        />
        <Reveal delay={0.15} className="mt-12 flex flex-wrap gap-4">
          {t("about.regions").map((region, i) => (
            <span
              key={i}
              data-testid={`region-badge-${i + 1}`}
              className="flex items-center gap-2.5 rounded-full border border-gold/30 bg-white/5 px-6 py-3 text-sm text-slate-200 backdrop-blur-sm"
            >
              <Globe className="h-4 w-4 text-gold" />
              {region}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

const Values = () => {
  const { t, c } = useLang();
  const values = c.aboutValues || VALUES_EN;
  return (
    <section data-testid="values-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader testid="values" overline={t("about.valuesOverline")} title={t("about.valuesTitle")} />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((val, i) => {
            const Icon = VALUES[i].icon;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`value-card-${i + 1}`}
                  className="card-lift h-full rounded-xl border border-obsidian/10 bg-white p-7 hover:border-gold/50 hover:shadow-lg"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-alabaster text-gold-muted">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-semibold text-obsidian">{val.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{val.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WhoWeWorkWith = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="about-who-we-work-with-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader testid="about-who-we-work-with" overline={t("about.whoOverline")} title={t("about.whoTitle")} />
        <div className="mt-16 grid gap-x-10 gap-y-0 sm:grid-cols-2 lg:grid-cols-3">
          {c.clients.map((type, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div data-testid={`about-client-type-${i + 1}`} className="border-t border-gold/30 py-6">
                <p className="font-mono text-xs tracking-[0.25em] text-gold-muted">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-2 font-serif text-xl text-obsidian">{type}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader />
      <FoundingStory />
      <Manifesto />
      <GlobalReach />
      <Values />
      <WhoWeWorkWith />
      <CTABanner />
    </div>
  );
}
